'use client'
// ═══════════════════════════════════════════════════════════════════════════
// « VOIR COMME LE CLIENT » — l'admin garde SA session.
//
// Pourquoi ce module existe (12/08/2026, demande fondateur) :
// `impersonation.js` ouvre une VRAIE session au nom de la cible
// (`supabase.auth.verifyOtp`). Supabase horodate alors `last_sign_in_at` sur le
// compte du client — exactement comme si le client s'était connecté lui-même.
// Conséquence mesurée sur KAP Group : le compte affichait une connexion « hier
// à 11h51 » qui était en réalité celle du fondateur en train de tester. Cette
// date ne dit donc plus RIEN de l'adoption réelle : impossible de savoir quel
// client a ouvert l'app une seule fois. Le fondateur, le 12/08 : « je dois
// vraiment savoir quand eux se connectent, pas quand moi je rentre sur leur app ».
//
// Ici, RIEN n'est écrit sur le compte du client :
//   • la session reste celle de l'admin (aucun token échangé) ;
//   • seul l'AFFICHAGE change — le menu et les écrans suivent le rôle simulé ;
//   • le dossier client, lui, passe par le sélecteur existant (SELECT_CLIENT),
//     source de vérité unique (cf. active-client-resolver.js).
//
// ⚠️ CE MODE CHANGE CE QUI EST AFFICHÉ, PAS LES DROITS SERVEUR.
// Les appels API partent toujours avec le jeton de l'admin, qui garde tous ses
// droits. C'est VOULU : ce qui est fait en « voir comme » reste tracé sous
// l'identité réelle de l'admin, jamais sous celle du client. Ne jamais présenter
// ce mode comme un bac à sable étanche.
// ═══════════════════════════════════════════════════════════════════════════

const KEY = 'aureus_view_as'

// Rôles simulables. Tous STRICTEMENT plus restreints que 'admin' : le mode ne
// peut donc jamais élargir ce qu'on voit. 'admin' est volontairement absent.
export const ROLES_SIMULABLES = ['employeur', 'rh_entreprise', 'employe', 'comptable']

/** État courant, ou null. Lisible côté client uniquement. */
export function getViewAs() {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    const v = JSON.parse(raw)
    // Un rôle hors liste (clé bricolée à la main) ne s'applique pas.
    return ROLES_SIMULABLES.includes(v?.role) ? v : null
  } catch { return null }
}

/**
 * Active le mode. À n'appeler QUE depuis un écran admin — le garde-fou qui
 * compte est côté layout (`realRole === 'admin'`), celui-ci n'est que le premier.
 * @param {{role: string, clientId?: string, clientNom?: string}} opts
 */
export function startViewAs({ role, clientId = null, clientNom = '' }) {
  if (typeof window === 'undefined') return
  if (!ROLES_SIMULABLES.includes(role)) throw new Error(`Rôle non simulable : ${role}`)
  sessionStorage.setItem(KEY, JSON.stringify({ role, clientId, clientNom, depuis: new Date().toISOString() }))
}

/** Événement fenêtre émis quand le client RÉELLEMENT chargé change (le bandeau, monté hors du dashboard, l'écoute). */
export const VIEW_AS_EVENT = 'aureus_view_as_change'

/**
 * Note, dans l'état du mode, le client que le dashboard a RÉELLEMENT chargé (29/10/2026).
 *
 * Pourquoi : le bandeau recopiait le nom du client au CLIC sur « Voir comme » (`clientNom`) et ne le relisait jamais ; `clientId` n'était lu
 * nulle part. Si le dossier chargé n'était pas celui du clic (dossier introuvable dans la liste, sélection restaurée d'une autre session,
 * changement de société dans le sélecteur), le bandeau affichait un nom FAUX. Le dashboard (layout-client.js, via use-view-as-sync.js) appelle
 * donc cette fonction avec ce qui est vraiment chargé ; `clientId / clientNom` de `startViewAs` restent ce qui a été DEMANDÉ.
 *
 * `charge` absent = le dashboard n'a encore rien dit (le bandeau n'affiche alors AUCUN nom) ; `charge.clientId === null` = aucun client chargé.
 * N'écrit rien si le mode n'est pas actif, ni si rien n'a changé. @returns {boolean} vrai si l'état a été modifié
 */
export function majClientViewAs({ clientId = null, clientNom = '' } = {}) {
  if (typeof window === 'undefined') return false
  const v = getViewAs()
  if (!v) return false
  const id = clientId ? String(clientId) : null
  const nom = id ? String(clientNom || '') : ''
  if (v.charge && v.charge.clientId === id && v.charge.clientNom === nom) return false
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...v, charge: { clientId: id, clientNom: nom } }))
  } catch { return false }
  try { window.dispatchEvent(new Event(VIEW_AS_EVENT)) } catch { /* l'état est écrit ; le bandeau le relira à son prochain rendu */ }
  return true
}

/** Coupe le mode. Ne touche ni à la session, ni au client sélectionné. */
export function stopViewAs() {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(KEY)
}

/**
 * Rôle à utiliser pour l'AFFICHAGE.
 * Le mode ne s'applique que si l'utilisateur RÉEL est admin : un employeur qui
 * poserait la clé à la main ne gagne rien (il ne peut de toute façon simuler
 * qu'un rôle plus restreint, et le serveur ne le croit pas sur parole).
 */
export function roleAffiche(realRole, viewAs, habilite = false) {
  if (realRole !== 'admin' && !habilite) return realRole
  return viewAs?.role || realRole
}

/**
 * Qui peut utiliser « Voir comme » : l'admin, et un compte habilité « tous les dossiers » EN LECTURE SEULE
 * (fondateur, 30/10/2026 : « elle doit pouvoir se connecter en tant que, sans rien pouvoir modifier »).
 * Le mode garde SA session : pour ce compte, le serveur refuse donc toute écriture sur les dossiers des autres
 * (all_clients_lecture, auth/role.js) — contrairement à une vraie session au nom du client, qui écrirait librement.
 * Lu dans app_metadata (service_role only), jamais dans user_metadata.
 */
export function peutVoirComme(user) {
  if (!user) return false
  if (user.app_metadata?.role === 'admin') return true
  return user.app_metadata?.all_clients === true && user.app_metadata?.all_clients_lecture === true
}
